import { AlertTriangle, Check, Circle, LoaderCircle, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button } from './Button'
import './progress.css'

export type LoadPartState = 'pending' | 'running' | 'done' | 'failed'

/** Один кусок чтения (месяц): подпись, состояние и пояснение («из кэша», «2,4 с», текст ошибки). */
export interface LoadPart {
  key: string
  /** «Окт 2026». */
  label: string
  state: LoadPartState
  detail?: string | null
}

const STATE_TEXT: Record<LoadPartState, string> = {
  pending: 'ждёт очереди',
  running: 'читается',
  done: 'прочитан',
  failed: 'не прочитан',
}

const STATE_ICON: Record<LoadPartState, ReactNode> = {
  pending: <Circle size={14} aria-hidden="true" />,
  running: <LoaderCircle size={14} className="krp-steps__spin" aria-hidden="true" />,
  done: <Check size={14} aria-hidden="true" />,
  failed: <AlertTriangle size={14} aria-hidden="true" />,
}

/**
 * Чтение периода по месяцам: у каждого месяца своё состояние — значок, форма и слово (ждёт, читается, прочитан, не прочитан), цвет лишь
 * усиливает. Подпись говорит, как читается: `sequential` — по очереди (живая база не получает все запросы разом), иначе — одновременно.
 * Когда всё прочитано и отказов нет, компонент ничего не рисует. Кнопка «Повторить» перечитывает только неудавшиеся месяцы.
 */
export function MonthLoadProgress({
  parts,
  sequential = true,
  onRetry,
}: {
  parts: readonly LoadPart[]
  sequential?: boolean
  onRetry?: () => void
}) {
  const done = parts.filter((part) => part.state === 'done').length
  const failed = parts.filter((part) => part.state === 'failed').length
  const running = parts.filter((part) => part.state === 'running')
  const waiting = parts.some((part) => part.state === 'pending' || part.state === 'running')
  if (parts.length < 2 || (!waiting && failed === 0)) return null
  const how = sequential ? 'по очереди, чтобы не перегружать базу' : 'одновременно'
  const current = running.length > 0 ? `сейчас — ${running.map((part) => part.label).join(', ')}` : null
  const caption = waiting
    ? `Читаю 1С ${how}: ${done} из ${parts.length}${current !== null ? ` · ${current}` : ''}`
    : `Не прочитано месяцев: ${failed} из ${parts.length} — итоги неполные`
  return (
    <section className="krp-load" aria-label="Чтение по месяцам" aria-busy={waiting || undefined}>
      <p className="krp-load__caption" role="status">
        {caption}
      </p>
      <ol className="krp-load__list">
        {parts.map((part) => (
          <li key={part.key} className="krp-load__item" data-state={part.state} title={part.detail ?? STATE_TEXT[part.state]}>
            {STATE_ICON[part.state]}
            <span>{part.label}</span>
            <span className="krp-load__state">{part.detail ?? STATE_TEXT[part.state]}</span>
          </li>
        ))}
      </ol>
      {failed > 0 && !waiting && onRetry !== undefined && (
        <div className="krp-load__actions">
          <Button variant="secondary" data-size="sm" icon={<RefreshCw size={14} aria-hidden="true" />} onClick={onRetry}>
            Повторить неудавшиеся
          </Button>
        </div>
      )}
    </section>
  )
}
